import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Our Services", href: "/services" },
  { label: "About Us", href: "/about" },
  { label: "Gallery", href: "/project-gallery" },
  { label: "Blog", href: "/blog", badge: "New" },
  { label: "Store", href: "/store", icon: "ri-store-2-line" },
  { label: "Contact Us", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className="fixed top-0 left-0 w-full z-50">
      {/* Top info strip — teal brand colour */}
      <div
        className={`w-full transition-all duration-300 overflow-hidden ${
          scrolled ? "h-0 opacity-0" : "h-8 opacity-100"
        }`}
      >
        <div className="bg-cyan-700 w-full h-8 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full flex items-center justify-between">
            <div className="flex items-center gap-6">
              <a
                href="tel:0724114555"
                className="flex items-center gap-1.5 text-white/90 text-xs hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xs"></i>
                072 411 4555
              </a>
              <a
                href="mailto:info@isman.co.ke"
                className="hidden sm:flex items-center gap-1.5 text-white/90 text-xs hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-xs"></i>
                info@isman.co.ke
              </a>
            </div>
            <div className="flex items-center gap-1.5 text-white/80 text-xs whitespace-nowrap">
              <i className="ri-map-pin-line text-xs"></i>
              Nairobi, Kenya
            </div>
          </div>
        </div>
      </div>

      {/* Main nav bar */}
      <div
        className={`relative w-full transition-all duration-300 ${
          scrolled
            ? "bg-white border-b border-gray-200 shadow-sm"
            : "bg-white/95 backdrop-blur-sm border-b border-gray-100"
        }`}
      >
        {/* Teal accent line at bottom */}
        <div
          className={`absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-cyan-500 via-teal-500 to-cyan-500 transition-opacity duration-300 ${
            scrolled ? "opacity-100" : "opacity-0"
          }`}
        ></div>

        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              handleNav("#home");
            }}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img
              src="/logo.png"
              alt="ISMAN Engineering Logo"
              className="h-10 w-auto object-contain"
            />
            <div>
              <h1 className="text-sm font-bold leading-tight uppercase tracking-wider text-cyan-600 group-hover:text-cyan-700 transition-colors">
                ISMAN Company
              </h1>
              <p className="text-xs tracking-widest uppercase text-gray-500 font-semibold">
                Engineering Services
              </p>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNav(link.href);
                }}
                className="relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-cyan-700 transition-colors cursor-pointer whitespace-nowrap group"
              >
                {link.icon && (
                  <i className={`${link.icon} text-sm text-cyan-600`}></i>
                )}
                {link.label}
                {link.badge && (
                  <span className="bg-cyan-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                    {link.badge}
                  </span>
                )}
                {/* animated underline */}
                <span className="absolute bottom-1 left-3 right-3 h-[2px] bg-cyan-500 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                handleNav("#contact");
              }}
              className="ml-3 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold px-5 py-2 rounded-md transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 shadow-sm"
            >
              <i className="ri-calendar-check-line text-sm"></i>
              Book Now
            </a>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-gray-700 hover:text-cyan-600 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i
              className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-1 shadow-md">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                handleNav(link.href);
              }}
              className="flex items-center gap-2 px-3 py-2.5 text-gray-700 hover:text-cyan-700 hover:bg-cyan-50 rounded-md font-medium text-sm transition-colors cursor-pointer"
            >
              {link.icon && (
                <i className={`${link.icon} text-cyan-600`}></i>
              )}
              {link.label}
              {link.badge && (
                <span className="bg-cyan-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {link.badge}
                </span>
              )}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              handleNav("#contact");
            }}
            className="mt-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold px-5 py-2.5 rounded-md text-center cursor-pointer flex items-center justify-center gap-1.5"
          >
            <i className="ri-calendar-check-line"></i>
            Book Now
          </a>
        </div>
      )}
    </header>
  );
}
